
export const root = {
    borderRadius: '20px',
    roundedBorderRadius: '2rem',
    gap: '0.5rem',
    paddingX: '1.125rem',
    paddingY: '0.375rem',
    fontSize: '0.875rem', // 14px
    fontWeight: '300', // Light weight — clean, modern feel
    iconOnlyWidth: '2.5rem',
    sm: {
        fontSize: '0.75rem', // 12px
        paddingX: '0.875rem',
        paddingY: '0.25rem',
        iconOnlyWidth: '2rem'
    },
    lg: {
        fontSize: '0.875rem', // 14px
        paddingX: '1.375rem',
        paddingY: '0.5rem',
        iconOnlyWidth: '2.75rem'
    },
    label: {
        fontWeight: '300'
    },
    raisedShadow: '0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12)',
    focusRing: {
        width: '2px',
        style: 'solid',
        color: '{primary.color}',
        offset: '2px',
        shadow: 'var(--p-focus-ring)'
    },
    badgeSize: '1rem',
    transitionDuration: '{transition.duration}'
};

export const colorScheme = {
    light: {
        root: {
            // Primary button: Outlined style (transparent bg, orange border/text)
            primary: {
                background: 'transparent',
                hoverBackground: 'rgba(239, 70, 0, 0.15)',
                activeBackground: 'rgba(239, 70, 0, 0.30)',
                borderColor: '{primary.color}',
                hoverBorderColor: '{primary.color}',
                activeBorderColor: '{primary.color}',
                color: '{primary.color}',
                hoverColor: '{primary.color}',
                activeColor: '{primary.color}',
                focusRing: {
                    color: 'transparent',
                    shadow: 'none'
                }
            },
            secondary: {
                background: '{surface.100}',
                hoverBackground: '{surface.200}',
                activeBackground: '{surface.300}',
                borderColor: '{surface.100}',
                hoverBorderColor: '{surface.200}',
                activeBorderColor: '{surface.300}',
                color: '{surface.600}',
                hoverColor: '{surface.700}',
                activeColor: '{surface.800}',
                focusRing: {
                    color: 'transparent',
                    shadow: '0 0 0 0.2rem {surface.200}'
                }
            },
            info: {
                background: '{sky.500}',
                hoverBackground: '{sky.600}',
                activeBackground: '{sky.700}',
                borderColor: '{sky.500}',
                hoverBorderColor: '{sky.600}',
                activeBorderColor: '{sky.700}',
                color: '#ffffff',
                hoverColor: '#ffffff',
                activeColor: '#ffffff',
                focusRing: {
                    color: 'transparent',
                    shadow: '0 0 0 0.2rem {sky.200}'
                }
            },
            success: {
                background: '{green.500}',
                hoverBackground: '{green.600}',
                activeBackground: '{green.700}',
                borderColor: '{green.500}',
                hoverBorderColor: '{green.600}',
                activeBorderColor: '{green.700}',
                color: '#ffffff',
                hoverColor: '#ffffff',
                activeColor: '#ffffff',
                focusRing: {
                    color: 'transparent',
                    shadow: '0 0 0 0.2rem {green.200}'
                }
            },
            warn: {
                background: '{orange.500}',
                hoverBackground: '{orange.600}',
                activeBackground: '{orange.700}',
                borderColor: '{orange.500}',
                hoverBorderColor: '{orange.600}',
                activeBorderColor: '{orange.700}',
                color: '#ffffff',
                hoverColor: '#ffffff',
                activeColor: '#ffffff',
                focusRing: {
                    color: 'transparent',
                    shadow: '0 0 0 0.2rem {orange.200}'
                }
            },
            help: {
                background: '{purple.500}',
                hoverBackground: '{purple.600}',
                activeBackground: '{purple.700}',
                borderColor: '{purple.500}',
                hoverBorderColor: '{purple.600}',
                activeBorderColor: '{purple.700}',
                color: '#ffffff',
                hoverColor: '#ffffff',
                activeColor: '#ffffff',
                focusRing: {
                    color: 'transparent',
                    shadow: '0 0 0 0.2rem {purple.200}'
                }
            },
            danger: {
                // Outlined style for danger - uses red.600 (true red, not pink)
                background: 'transparent',
                hoverBackground: 'rgba(220, 38, 38, 0.05)',
                activeBackground: 'rgba(220, 38, 38, 0.1)',
                borderColor: '{red.600}',
                hoverBorderColor: '{red.600}',
                activeBorderColor: '{red.600}',
                color: '{red.600}',
                hoverColor: '{red.600}',
                activeColor: '{red.600}',
                focusRing: {
                    color: 'transparent',
                    shadow: 'none'
                }
            },
            contrast: {
                background: '{surface.950}',
                hoverBackground: '{surface.900}',
                activeBackground: '{surface.800}',
                borderColor: '{surface.950}',
                hoverBorderColor: '{surface.900}',
                activeBorderColor: '{surface.800}',
                color: '{surface.0}',
                hoverColor: '{surface.0}',
                activeColor: '{surface.0}',
                focusRing: {
                    color: 'transparent',
                    shadow: '0 0 0 0.2rem {surface.400}'
                }
            }
        },
        outlined: {
            primary: {
                hoverBackground: 'rgba(239, 70, 0, 0.15)',
                activeBackground: 'rgba(239, 70, 0, 0.30)',
                borderColor: '{primary.color}',
                color: '{primary.color}'
            },
            secondary: {
                hoverBackground: '{surface.50}',
                activeBackground: '{surface.100}',
                borderColor: '{surface.200}',
                color: '{surface.500}'
            },
            success: {
                hoverBackground: '{green.50}',
                activeBackground: '{green.100}',
                borderColor: '{green.200}',
                color: '{green.500}'
            },
            info: {
                hoverBackground: '{sky.50}',
                activeBackground: '{sky.100}',
                borderColor: '{sky.200}',
                color: '{sky.500}'
            },
            warn: {
                hoverBackground: '{orange.50}',
                activeBackground: '{orange.100}',
                borderColor: '{orange.200}',
                color: '{orange.500}'
            },
            help: {
                hoverBackground: '{purple.50}',
                activeBackground: '{purple.100}',
                borderColor: '{purple.200}',
                color: '{purple.500}'
            },
            danger: {
                hoverBackground: '{red.50}',
                activeBackground: '{red.100}',
                borderColor: '{red.200}',
                color: '{red.500}'
            },
            contrast: {
                hoverBackground: '{surface.50}',
                activeBackground: '{surface.100}',
                borderColor: '{surface.700}',
                color: '{surface.950}'
            },
            plain: {
                hoverBackground: '{surface.50}',
                activeBackground: '{surface.100}',
                borderColor: '{surface.200}',
                color: '{surface.700}'
            }
        },
        text: {
            // Text buttons use muted colors — semi-transparent hover for subtlety
            primary: {
                hoverBackground: '{surface.hover}',
                activeBackground: '{surface.active}',
                color: '{text.secondary.color}'
            },
            secondary: {
                hoverBackground: '{surface.hover}',
                activeBackground: '{surface.active}',
                color: '{surface.600}'
            },
            success: {
                hoverBackground: '{green.50}',
                activeBackground: '{green.100}',
                color: '{green.500}'
            },
            info: {
                hoverBackground: '{sky.50}',
                activeBackground: '{sky.100}',
                color: '{sky.500}'
            },
            warn: {
                hoverBackground: '{orange.50}',
                activeBackground: '{orange.100}',
                color: '{orange.500}'
            },
            help: {
                hoverBackground: '{purple.50}',
                activeBackground: '{purple.100}',
                color: '{purple.500}'
            },
            danger: {
                hoverBackground: '{red.50}',
                activeBackground: '{red.100}',
                color: '{red.600}'
            },
            contrast: {
                hoverBackground: '{surface.50}',
                activeBackground: '{surface.100}',
                color: '{surface.950}'
            },
            plain: {
                hoverBackground: '{surface.50}',
                activeBackground: '{surface.100}',
                color: '{surface.700}'
            }
        },
        link: {
            color: '{primary.color}',
            hoverColor: '{primary.color}',
            activeColor: '{primary.color}'
        }
    },
    dark: {
        root: {
            // Primary button: Outlined style (transparent bg, orange border/text)
            primary: {
                background: 'transparent',
                hoverBackground: 'rgba(239, 70, 0, 0.15)',
                activeBackground: 'rgba(239, 70, 0, 0.30)',
                borderColor: '{primary.color}',
                hoverBorderColor: '{primary.color}',
                activeBorderColor: '{primary.color}',
                color: '{primary.color}',
                hoverColor: '{primary.color}',
                activeColor: '{primary.color}',
                focusRing: {
                    color: 'transparent',
                    shadow: 'none'
                }
            },
            secondary: {
                background: '{surface.800}',
                hoverBackground: '{surface.700}',
                activeBackground: '{surface.600}',
                borderColor: '{surface.800}',
                hoverBorderColor: '{surface.700}',
                activeBorderColor: '{surface.600}',
                color: '{surface.300}',
                hoverColor: '{surface.200}',
                activeColor: '{surface.100}',
                focusRing: {
                    color: 'transparent',
                    shadow: '0 0 0 0.2rem color-mix(in srgb, {surface.300}, transparent 80%)'
                }
            },
            info: {
                background: '{sky.400}',
                hoverBackground: '{sky.300}',
                activeBackground: '{sky.200}',
                borderColor: '{sky.400}',
                hoverBorderColor: '{sky.300}',
                activeBorderColor: '{sky.200}',
                color: '{sky.950}',
                hoverColor: '{sky.950}',
                activeColor: '{sky.950}',
                focusRing: {
                    color: 'transparent',
                    shadow: '0 0 0 0.2rem color-mix(in srgb, {sky.400}, transparent 80%)'
                }
            },
            success: {
                background: '{green.400}',
                hoverBackground: '{green.300}',
                activeBackground: '{green.200}',
                borderColor: '{green.400}',
                hoverBorderColor: '{green.300}',
                activeBorderColor: '{green.200}',
                color: '{green.950}',
                hoverColor: '{green.950}',
                activeColor: '{green.950}',
                focusRing: {
                    color: 'transparent',
                    shadow: '0 0 0 0.2rem color-mix(in srgb, {green.400}, transparent 80%)'
                }
            },
            warn: {
                background: '{orange.400}',
                hoverBackground: '{orange.300}',
                activeBackground: '{orange.200}',
                borderColor: '{orange.400}',
                hoverBorderColor: '{orange.300}',
                activeBorderColor: '{orange.200}',
                color: '{orange.950}',
                hoverColor: '{orange.950}',
                activeColor: '{orange.950}',
                focusRing: {
                    color: 'transparent',
                    shadow: '0 0 0 0.2rem color-mix(in srgb, {orange.400}, transparent 80%)'
                }
            },
            help: {
                background: '{purple.400}',
                hoverBackground: '{purple.300}',
                activeBackground: '{purple.200}',
                borderColor: '{purple.400}',
                hoverBorderColor: '{purple.300}',
                activeBorderColor: '{purple.200}',
                color: '{purple.950}',
                hoverColor: '{purple.950}',
                activeColor: '{purple.950}',
                focusRing: {
                    color: 'transparent',
                    shadow: '0 0 0 0.2rem color-mix(in srgb, {purple.400}, transparent 80%)'
                }
            },
            danger: {
                // Outlined style for danger - uses red.600 (deep true red)
                background: 'transparent',
                hoverBackground: 'rgba(220, 38, 38, 0.05)',
                activeBackground: 'rgba(220, 38, 38, 0.1)',
                borderColor: '{red.600}',
                hoverBorderColor: '{red.600}',
                activeBorderColor: '{red.600}',
                color: '{red.600}',
                hoverColor: '{red.600}',
                activeColor: '{red.600}',
                focusRing: {
                    color: 'transparent',
                    shadow: 'none'
                }
            },
            contrast: {
                background: '{surface.0}',
                hoverBackground: '{surface.100}',
                activeBackground: '{surface.200}',
                borderColor: '{surface.0}',
                hoverBorderColor: '{surface.100}',
                activeBorderColor: '{surface.200}',
                color: '{surface.950}',
                hoverColor: '{surface.950}',
                activeColor: '{surface.950}',
                focusRing: {
                    color: 'transparent',
                    shadow: '0 0 0 0.2rem color-mix(in srgb, {surface.0}, transparent 80%)'
                }
            }
        },
        outlined: {
            primary: {
                hoverBackground: 'rgba(239, 70, 0, 0.15)',
                activeBackground: 'rgba(239, 70, 0, 0.30)',
                borderColor: '{primary.color}',
                color: '{primary.color}'
            },
            secondary: {
                hoverBackground: 'rgba(255,255,255,0.04)',
                activeBackground: 'rgba(255,255,255,0.16)',
                borderColor: '{surface.700}',
                color: '{surface.400}'
            },
            success: {
                hoverBackground: 'color-mix(in srgb, {green.400}, transparent 96%)',
                activeBackground: 'color-mix(in srgb, {green.400}, transparent 84%)',
                borderColor: '{green.700}',
                color: '{green.400}'
            },
            info: {
                hoverBackground: 'color-mix(in srgb, {sky.400}, transparent 96%)',
                activeBackground: 'color-mix(in srgb, {sky.400}, transparent 84%)',
                borderColor: '{sky.700}',
                color: '{sky.400}'
            },
            warn: {
                hoverBackground: 'color-mix(in srgb, {orange.400}, transparent 96%)',
                activeBackground: 'color-mix(in srgb, {orange.400}, transparent 84%)',
                borderColor: '{orange.700}',
                color: '{orange.400}'
            },
            help: {
                hoverBackground: 'color-mix(in srgb, {help.400}, transparent 96%)',
                activeBackground: 'color-mix(in srgb, {help.400}, transparent 84%)',
                borderColor: '{purple.700}',
                color: '{purple.400}'
            },
            danger: {
                hoverBackground: 'color-mix(in srgb, {red.600}, transparent 85%)',
                activeBackground: 'color-mix(in srgb, {red.600}, transparent 75%)',
                borderColor: '{red.600}',
                color: '{red.600}'
            },
            contrast: {
                hoverBackground: '{surface.800}',
                activeBackground: '{surface.700}',
                borderColor: '{surface.500}',
                color: '{surface.0}'
            },
            plain: {
                hoverBackground: '{surface.800}',
                activeBackground: '{surface.700}',
                borderColor: '{surface.600}',
                color: '{surface.0}'
            }
        },
        text: {
            primary: {
                hoverBackground: '{surface.hover}',
                activeBackground: '{surface.active}',
                color: '{text.secondary.color}'
            },
            // The second text colour, as the light scheme's value is. A surface step is never a text colour on
            // the dark side: the dark ramp ascends, and step 300 is a dark grey (1.9 : 1 on a card) — it left the
            // date picker's arrows, time steppers and Today / Clear, which PrimeNG draws with this kind, unreadable.
            secondary: {
                hoverBackground: '{surface.hover}',
                activeBackground: '{surface.active}',
                color: '{text.secondary.color}'
            },
            success: {
                hoverBackground: 'color-mix(in srgb, {green.400}, transparent 96%)',
                activeBackground: 'color-mix(in srgb, {green.400}, transparent 84%)',
                color: '{green.400}'
            },
            info: {
                hoverBackground: 'color-mix(in srgb, {sky.400}, transparent 96%)',
                activeBackground: 'color-mix(in srgb, {sky.400}, transparent 84%)',
                color: '{sky.400}'
            },
            warn: {
                hoverBackground: 'color-mix(in srgb, {orange.400}, transparent 96%)',
                activeBackground: 'color-mix(in srgb, {orange.400}, transparent 84%)',
                color: '{orange.400}'
            },
            help: {
                hoverBackground: 'color-mix(in srgb, {purple.400}, transparent 96%)',
                activeBackground: 'color-mix(in srgb, {purple.400}, transparent 84%)',
                color: '{purple.400}'
            },
            danger: {
                hoverBackground: 'color-mix(in srgb, {red.600}, transparent 85%)',
                activeBackground: 'color-mix(in srgb, {red.600}, transparent 75%)',
                color: '{red.600}'
            },
            contrast: {
                hoverBackground: '{surface.800}',
                activeBackground: '{surface.700}',
                color: '{surface.0}'
            },
            plain: {
                hoverBackground: '{surface.800}',
                activeBackground: '{surface.700}',
                color: '{surface.0}'
            }
        },
        link: {
            color: '{primary.color}',
            hoverColor: '{primary.color}',
            activeColor: '{primary.color}'
        }
    }
};

// CSS overrides — PrimeNG hardcodes font-size: 1rem on .p-button.
// The css callback injects after base component CSS, overriding the hardcode.
// .p-button-filled: opt-in filled-primary variant (the app's base primary button is
// outlined by design) — tokens only, adopted by the comment composer's Post button.
// The text variant stays BORDERLESS: it is chrome — a menu trigger, a close, a carousel's arrows, a
// date picker's month arrows — never an action door. A door a member acts on is a real button, the
// outlined variant (Robert's ruling of 2026-09-18: a borderless "Translate" read as a stray word, and
// "only buttons that are really buttons" wear a border — a theme rule over the text variant reached
// the carousel arrows and the account trigger, so the rule lives in the door's variant, not here).
export const css = ({dt}: {dt: (token: string) => string}) => `
.p-button {
    font-size: ${dt('button.font.size')};
    font-weight: ${dt('button.font.weight')};
}
.p-button.p-button-filled {
    background: ${dt('primary.color')};
    border-color: ${dt('primary.color')};
    color: ${dt('primary.contrast.color')};
}
.p-button.p-button-filled:not(:disabled):hover {
    background: ${dt('primary.hover.color')};
    border-color: ${dt('primary.hover.color')};
    color: ${dt('primary.contrast.color')};
}
`;

export default {
    root,
    colorScheme,
    css
};
